import React, { useEffect, useState } from 'react'

import getMakes, { GetMakesOptions, Make } from 'api/vehicles/getMakes'
import { FormikDropdown } from 'components/FormikFields'
import { FormikDropdownChild } from 'components/FormikFields/FormikDropdown/FormikDropdown'
import { FieldKit } from 'containers/FormBuilder'
import { FormikValues } from 'formik'
import AssetType from 'state/enums/AssetType'

export interface VehicleMakeDropdownProps {
    formik: FieldKit<FormikValues>
    assetType: AssetType
    dataSource?: string[]
    prefilled?: boolean
}

const defaultProps = {
    dataSource: null,
    prefilled: false,
}

export default function MakeDropdown({
    formik,
    assetType,
    dataSource,
    prefilled,
}: Readonly<VehicleMakeDropdownProps>) {
    const [response, setResponse] = useState([] as Make[])
    const { vehicle } = formik.values
    const { condition, year, makeId } = vehicle
    const hasSelectedYear = Boolean(year) && year !== '0'

    useEffect(() => {
        const abortController = new AbortController()

        if (condition && hasSelectedYear) {
            const getMakesOptions = {
                condition,
                year,
                dataSource,
                assetType,
            } as GetMakesOptions

            setResponse([])

            getMakes(getMakesOptions, abortController)
                .then((makes: Make[]) => {
                    setResponse(makes)

                    const selectedMake =
                        makes.find((make) => make.id.toString() === makeId) ??
                        makes.find(
                            (make) =>
                                make.name.toLowerCase() ===
                                vehicle.make?.toLowerCase(),
                        )

                    if (selectedMake) {
                        if (makeId !== selectedMake.id.toString()) {
                            formik.setFieldValue(
                                'vehicle.makeId',
                                selectedMake.id.toString(),
                            )
                        }

                        if (vehicle.make !== selectedMake.name) {
                            formik.setFieldValue(
                                'vehicle.make',
                                selectedMake.name,
                            )
                        }
                    } else {
                        formik.setFieldValue('vehicle.makeId', '')
                        formik.setFieldValue('vehicle.make', '')
                        formik.setFieldValue('vehicle.modelId', '')
                        formik.setFieldValue('vehicle.model', '')
                        formik.setFieldValue('vehicle.automobileId', '')
                        formik.setFieldValue('vehicle.trim', '')
                    }
                })
                .catch(() => {})
        }

        return () => abortController.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condition, year])

    useEffect(() => {
        const selectedMake = response.find(
            (make) => make.id.toString() === makeId,
        )

        if (selectedMake) {
            formik.setFieldValue('vehicle.make', selectedMake.name)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [makeId])

    if (!hasSelectedYear) {
        return (
            <FormikDropdown
                name="vehicle.makeId"
                label="Make"
                placeholder="Select One"
                disabled
                prefilled={prefilled}
            >
                {[{ name: '', value: '' }]}
            </FormikDropdown>
        )
    }

    return (
        <FormikDropdown
            name="vehicle.makeId"
            label="Make"
            placeholder="Select One"
            disabled={response.length === 0}
            error={makeId === null || makeId === ''}
            searchable
            searchPlaceholder="Search makes..."
            prefilled={prefilled}
        >
            {response.map(
                (child: Make): FormikDropdownChild => ({
                    name: child.name,
                    value: child.id.toString(),
                }),
            )}
        </FormikDropdown>
    )
}

MakeDropdown.defaultProps = defaultProps
